import Vue from 'vue';
import './pollyfills';
import VueRouter from 'vue-router';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import VueRouterPrefetch from 'vue-router-prefetch';
import VueNotify from 'vue-notifyjs';
import VeeValidate from 'vee-validate';
import locale from 'element-ui/lib/locale/lang/en';
import ElementUI from 'element-ui';
import VueMoment from 'vue-moment';
// import makeStore from './store';
import VueAnalytics from 'vue-analytics';
import vSelect from 'vue-select';
import mixins from './mixins';
import App from './App.vue';
import store from './store';
import apiService from './repositories/apiService.js';
import 'vue-select/dist/vue-select.css';

// Plugins
import GlobalComponents from './globalComponents';
import GlobalDirectives from './globalDirectives';
import SideBar from './components/UIComponents/SidebarPlugin';
import initProgress from './progressbar';

// router setup
import routes from './routes/routes';

// library imports
import './assets/sass/paper-dashboard.scss';
import './assets/sass/demo.scss';
import './assets/sass/reckoning.scss';
import './assets/sass/editor.scss';

import 'font-awesome/css/font-awesome.min.css';

import sidebarLinks from './sidebarLinks';
import './registerServiceWorker';

// Service Container
import ServiceContainer from './services/serviceContainer';

Bugsnag.start({
    apiKey: process.env.VUE_APP_BUGSNAG,
    plugins: [new BugsnagPluginVue()],
    releaseStage: process.env.NODE_ENV
});
Bugsnag.getPlugin('vue')
    .installVueErrorHandler(Vue);

// Axios interceptors are configured in apiService.js for Sanctum session-based auth
// No need to add Bearer token headers - Sanctum uses cookies

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
    return originalPush.call(this, location).catch((err) => err);
};

require('vue-moment');

Vue.prototype.$http = apiService;

// plugin setup
Vue.mixin(mixins);
Vue.use(VueRouter);
Vue.use(VueRouterPrefetch);
Vue.use(GlobalDirectives);
Vue.use(GlobalComponents);
Vue.use(VueNotify);
Vue.use(SideBar, { sidebarLinks });
Vue.use(VueMoment);
Vue.component('v-select', vSelect);

Vue.use(ElementUI, { locale });

Vue.use(VeeValidate, {
    fieldsBagName: 'vvFields'
});

// const store = makeStore(ServiceContainer);

// configure router
const router = new VueRouter({
    mode: 'history',
    base: '/',
    routes, // short for routes: routes
    linkActiveClass: 'active',
    scrollBehavior: (to) => {
        if (to.hash) {
            return { selector: to.hash };
        }
        return { x: 0, y: 0 };
    }
});

// Configuration VueAnalytics
Vue.use(VueAnalytics, {
    id: 'UA-2678081-41',
    router
});

// Routes can be limited to roles or role groups with meta.roles, checked the same way
// the sidebar decides which links to show
function canOpenRoute(route) {
    const roles = route.matched.map((record) => record.meta.roles).find((r) => r);
    return !roles || mixins.methods.isOneOfTheseRoles.call({ $store: store }, roles);
}

router.beforeEach((to, from, next) => {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
    if (requiresAuth) {
        // For Sanctum session-based auth, we need to check if user has a valid session
        // Try to get the role - if successful, user is authenticated
        if (store.state.role === null) {
            store.dispatch('getRole')
                .then(() => {
                    // User is authenticated, get locations if needed
                    if (!canOpenRoute(to)) {
                        next({ name: 'accessdenied' });
                    } else if (store.state.userLocations === null) {
                        store.dispatch('getUserLocations').then(() => next());
                    } else {
                        next();
                    }
                })
                .catch((error) => {
                    // Not authenticated or error - redirect to login
                    next('/login');
                });
        } else if (!canOpenRoute(to)) {
            next({ name: 'accessdenied' });
        } else if (store.state.userLocations === null) {
            // Role already in store, user is authenticated
            store.dispatch('getUserLocations').then(() => next());
        } else {
            next();
        }
    } else {
        next();
    }
});

initProgress(router);

/* eslint-disable no-new */
new Vue({
    el: '#app',
    store,
    render: (h) => h(App),
    router,
    provide: ServiceContainer
});
